import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'utils/hooks/useAuth';
import { useObjectRef } from 'utils/hooks/useObjectRef';
import { useLocationSearchParameters } from 'utils/hooks/useLocationSearchParameters';
import { noop } from 'functions/noop';
import { LOCAL_STORAGE_KEYS } from 'utils/constants/localStorageKeys';
import { DETACH_URL_SEARCH_PARAM, SOURCE_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { preventIframeInteraction } from 'new-ui/Components/OpenInNewTab';
import { appConfigurationActions } from 'modules/appConfiguration/actions';
import { detachUserProvider } from 'modules/user/api';
import { updateIntercomToken } from 'modules/intercom/api';
import { POPUPS } from 'utils/constants/popup';
import {
  answerQuestionAction,
  showUrlPopupAction,
  questionToAskAction,
  questionsToAskAction,
  unsubscribeFormAction,
} from './actions';

const POPUP_DISPLAYING_TIMEOUT = 1000;

const PROVIDER_DETACH_COMMENT = 'user requested to see the full list after getting no results';

export const useAuthChanged = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const { data: profile } = useSelector((state) => state.profile);

  const searchParameters = useLocationSearchParameters();

  const searchParametersRef = useObjectRef(searchParameters);

  const { [DETACH_URL_SEARCH_PARAM]: detachSearchParam } = searchParameters;

  const { personal: { providerCode } = {} } = profile ?? {};
  const shouldDetach = !!detachSearchParam;

  // runs after user authentication
  useEffect(() => {
    if (!isAuthenticated) {
      return noop;
    }

    let popupTimeout;

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.SHOULD_CREATE_LOGIN_TOKEN)) {
      updateIntercomToken().catch(noop);
    }

    const {
      q: questionToSetSearchParam,
      answer: answerSearchParam,
      toast: toastSearchParam,
      redirect_popup: redirectPopupSearchParam,
      popup: popupSearchParam,
      popup_data: popupDataSearchParam,
      [SOURCE_URL_SEARCH_PARAM]: sourceSearchParam,
      question: questionToAskSearchParam,
      questions: questionsToAskSearchParam,
      unsubscribe_form: unsubscribeFormSearchParam,
    } = searchParametersRef.current;

    const questionsToAsk = (questionsToAskSearchParam ?? '').split(',').filter((i) => i);

    if (questionToSetSearchParam && answerSearchParam) {
      dispatch(answerQuestionAction({
        question: questionToSetSearchParam,
        answer: answerSearchParam,
        toastText: toastSearchParam,
        redirectPopup: redirectPopupSearchParam,
      }));
    }

    if (popupSearchParam && POPUPS.includes(popupSearchParam)) {
      popupTimeout = setTimeout(() => {
        popupTimeout = null;
        dispatch(showUrlPopupAction({
          type: popupSearchParam,
          data: { ...(popupDataSearchParam ?? {}), source: sourceSearchParam },
        }));
      }, POPUP_DISPLAYING_TIMEOUT);
    } else if (questionToAskSearchParam) {
      popupTimeout = setTimeout(() => {
        popupTimeout = null;
        dispatch(questionToAskAction({ question: questionToAskSearchParam }));
      }, POPUP_DISPLAYING_TIMEOUT);
    } else if (questionsToAsk.length) {
      popupTimeout = setTimeout(() => {
        popupTimeout = null;
        dispatch(questionsToAskAction({ questions: questionsToAsk }));
      }, POPUP_DISPLAYING_TIMEOUT);
    } else if (unsubscribeFormSearchParam) {
      popupTimeout = setTimeout(() => {
        popupTimeout = null;
        dispatch(unsubscribeFormAction());
      }, POPUP_DISPLAYING_TIMEOUT);
    }

    return () => {
      if (popupTimeout) {
        clearTimeout(popupTimeout);
      }
    };
  }, [dispatch, isAuthenticated, searchParametersRef]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (document.referrer === 'https://www.pfizerclinicaltrials.com/' && !providerCode) {
      preventIframeInteraction();
    }
  }, [isAuthenticated, providerCode]);

  useEffect(() => {
    if (!isAuthenticated || !shouldDetach) {
      return;
    }
    detachUserProvider({ auditComment: PROVIDER_DETACH_COMMENT }).catch(noop);
  }, [isAuthenticated, shouldDetach]);

  useEffect(() => {
    dispatch(appConfigurationActions.getConfiguration());
  }, [dispatch, isAuthenticated, providerCode]);
};
